import { FnDesc, FnParameter, SimpleParameter } from "../types";

export type FnParameterType = FnParameter["value_type"];

// Every parameter type the run API accepts.
// TODO: Replace this with a generated type from frontend_types.py
export const FN_PARAMETER_TYPES: ReadonlyArray<FnParameterType> = [
  "string",
  "integer",
  "number",
  "boolean",
  "null",
  "array",
  "object",
];

// The run API's FunctionDescription.name constraint.
const FN_NAME_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
export const isValidFunctionName = (name: string): boolean =>
  FN_NAME_PATTERN.test(name);

// A fresh name from the base, numbered past any collision.
// Why a function can't run as named, or null: the run API constrains the
// name, and a duplicate would leave the agent picking between namesakes.
export const functionNameError = (
  functions: Array<FnDesc>,
  index: number
): string | null => {
  const name = functions[index]?.name ?? "";
  if (!isValidFunctionName(name)) {
    return "Use 1–64 letters, digits, _ or -.";
  }
  return functions.some(
    (other, candidate) => candidate !== index && other.name === name
  )
    ? "Another function has this name."
    : null;
};

export const hasValidFunctionNames = (functions: Array<FnDesc>): boolean =>
  functions.every((_, index) => functionNameError(functions, index) === null);

// Generate a unique name from a base name and a list of taken names.
export const nextName = (base: string, taken: Iterable<string>): string => {
  const names = new Set(taken);
  if (!names.has(base)) {
    return base;
  }
  let ordinal = 2;
  while (names.has(`${base}_${ordinal}`)) {
    ordinal += 1;
  }
  return `${base}_${ordinal}`;
};

// Generate a new function with a unique name.
export const newFunction = (functions: Array<FnDesc>): FnDesc => ({
  name: nextName(
    "new_function",
    functions.map((fn) => fn.name)
  ),
  docs: "",
  parameters: {},
  required: [],
});

// Generate a new parameter with a default value of "string".
export const newParameter = (): SimpleParameter => ({
  value_type: "string",
  docs: null,
});

// Convert a parameter to another type, keeping only its docs — the
// enumeration, bounds, and children all describe the old type's values.
export const convertParameter = (
  parameter: FnParameter,
  valueType: FnParameterType
): FnParameter => {
  if (valueType === parameter.value_type) {
    return parameter;
  }
  if (valueType === "array") {
    return { value_type: "array", docs: parameter.docs, items: newParameter() };
  }
  if (valueType === "object") {
    return {
      value_type: "object",
      docs: parameter.docs,
      properties: {},
      required: [],
    };
  }
  return { value_type: valueType, docs: parameter.docs };
};

// A parameter map with its required-names list. A FnDesc's top level and an
// ObjectParameter share this shape, so the argument editors handle both.
export type FnParameterMap = {
  parameters: Record<string, FnParameter>;
  required: Array<string>;
};

export const addParameter = (
  map: FnParameterMap,
  base: string
): FnParameterMap => ({
  parameters: {
    ...map.parameters,
    [nextName(base, Object.keys(map.parameters))]: newParameter(),
  },
  required: map.required,
});

export const renameParameter = (
  map: FnParameterMap,
  name: string,
  next: string
): FnParameterMap => ({
  parameters: Object.fromEntries(
    Object.entries(map.parameters).map(([key, parameter]) => [
      key === name ? next : key,
      parameter,
    ])
  ),
  required: map.required.map((key) => (key === name ? next : key)),
});

export const deleteParameter = (
  map: FnParameterMap,
  name: string
): FnParameterMap => ({
  parameters: Object.fromEntries(
    Object.entries(map.parameters).filter(([key]) => key !== name)
  ),
  required: map.required.filter((key) => key !== name),
});

export const setParameterRequired = (
  map: FnParameterMap,
  name: string,
  required: boolean
): FnParameterMap => ({
  parameters: map.parameters,
  required: required
    ? [...map.required.filter((key) => key !== name), name]
    : map.required.filter((key) => key !== name),
});

// Parse one typed-in enumeration value as the parameter's type; null when
// it is blank or does not parse, which keeps it out of the list.
export const parseEnumerationValue = (
  valueType: "string" | "integer" | "number",
  text: string
): string | number | null => {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  if (valueType === "string") {
    return trimmed;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  if (valueType === "integer" && !Number.isInteger(parsed)) {
    return null;
  }
  return parsed;
};
